import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Leaf, Play, Plus, ArrowRight, Star, HeartPulse, Moon, Compass, ScrollText, Music, Unlock, Sparkles } from "lucide-react";
import { Nav, Footer } from "./Nav";
import { Card } from "./Card";
import { useShop } from "./useShop";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";
import { MOODS, PRACTITIONER, TESTIMONIALS, MAAT_LAWS, BROWSE_GOALS, BROWSE_TRADITIONS, BROWSE_MOODS } from "../data/mock";
import { EventBanner } from "./EventPromo";
import { Seo, orgJsonLd, SITE_URL } from "./Seo";

const MOOD_ICONS = { HeartPulse, Moon, Compass, ScrollText, Music, Unlock };

export function Home() {
  const { catalog, wishedIds, toggleWish, buy, partner, openItem, navigate } = useShop();
  const { user, refresh } = useAuth();
  const [progress, setProgress] = useState([]);
  const [email, setEmail] = useState("");

  useEffect(() => { if (user) api.get("/progress").then((r) => setProgress(r.data)).catch(() => {}); }, [user]);

  const has = (id) => catalog.find((c) => c.id === id);
  const byCat = (c) => catalog.filter((x) => x.category === c);
  const byMood = (m) => catalog.filter((x) => (x.moods || []).includes(m));
  const byIds = (ids) => ids.map(has).filter(Boolean);

  const trending = useMemo(() => [...catalog].filter((c) => c.category !== "membership").sort((a, b) => b.rating - a.rating).slice(0, 10), [catalog]);
  const community = useMemo(() => catalog.filter((c) => c.rating >= 4.8 && c.category !== "membership").slice(0, 10), [catalog]);
  const evanItems = byIds(["nlp_practitioner", "nlp_coaching", "sitai", "breakthrough", "reiki_group"]);
  const contInProgress = progress.map((p) => ({ item: has(p.item_id), percent: p.percent })).filter((x) => x.item);

  // Stripe return
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) return;
    let n = 0; const clean = () => window.history.replaceState({}, "", "/");
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sid}`);
        if (data.payment_status === "paid") { toast.success(`Unlocked — “${data.title || "your item"}”`); await refresh(); clean(); return; }
        if (data.status === "expired" || n >= 5) { toast.error("Payment not completed."); clean(); return; }
        n += 1; setTimeout(poll, 2000);
      } catch { clean(); }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cp = { wishedIds, onWish: toggleWish, onBuy: buy, onPartner: partner, onOpen: openItem };
  const Row = ({ title, tagline, color = "gold", items, to, ranked }) => (
    items && items.length ? (
      <section className="sec"><div className="wrap">
        <div className={`row-head ${color}`}>
          <span className="bar" /><h2>{title}</h2><span className="tagline">{tagline}</span>
          {to && <Link className="text-btn" to={to} style={{ marginLeft: "auto" }}>View all →</Link>}
        </div>
        <div className="card-row">
          {items.map((it, i) => <Card key={(it.item ? it.item.id : it.id) + i} item={it.item || it} progress={it.percent} rank={ranked ? i + 1 : undefined} wished={wishedIds.includes((it.item || it).id)} {...cp} />)}
        </div>
      </div></section>
    ) : null
  );

  const submitLead = async (e) => { e.preventDefault(); try { await api.post("/leads", { email }); toast.success("Thank you — we'll be in touch"); setEmail(""); } catch { toast.error("Please try again."); } };

  return (
    <>
      <Seo description="Ely Wellbeing — a training centre & therapy hub blending modern practice with the ancient Wu'sabat path. NLP Practitioner certification, therapies, retreats and gated learning." jsonLd={[orgJsonLd, { "@context": "https://schema.org", "@type": "WebSite", name: "Ely Wellbeing", url: SITE_URL }]} />
      <Nav />
      <EventBanner />
      <section className="hero">
        <div className="wrap hero-inner">
          <span className="badge-gold">MUS BAT-U</span>
          <h1 style={{ marginTop: "1rem" }}>Know Thyself,<br />Heal Thyself.</h1>
          <p className="lead">A sanctuary for holistic healing — blending modern therapy with the ancient Egyptian Wu'sabat path. Truth, balance, order and reciprocity, for everyday living.</p>
          <div className="actions">
            <button className="btn btn-primary" onClick={() => document.getElementById("moods").scrollIntoView()}><Play size={16} /> Begin</button>
            <Link className="btn btn-gold-outline" to="/wusabat"><Plus size={16} /> My List</Link>
          </div>
          <div className="as-above">“As above, so below.”</div>
        </div>
      </section>

      {user && contInProgress.length ? <Row title="Continue Your Journey" tagline="Pick up where you left off." color="gold" items={contInProgress} /> : null}
      <Row title="Trending in Wellness" tagline="What the community is loving right now." color="red" items={trending} ranked />

      {/* Mood */}
      <section className="sec pad" id="moods"><div className="wrap">
        <div className="row-head green"><span className="bar" /><h2>Where shall we start today?</h2><span className="tagline">Begin with how you feel.</span></div>
        <div className="mood-grid">
          {MOODS.map((m) => { const Icon = MOOD_ICONS[m.icon] || Sparkles; return (
            <button key={m.id} className="mood" onClick={() => navigate(`/explore?mood=${m.id}`)}>
              <span className="m-icon"><Icon size={20} /></span><strong>{m.label}</strong><small>{m.desc}</small>
            </button>); })}
        </div>
      </div></section>

      {/* Musbat-u band */}
      <section className="sec"><div className="wrap">
        <div className="band green">
          <span className="badge-gold">MUS BAT-U</span>
          <h2 style={{ color: "#fff", marginTop: ".8rem" }}>Walk the Path of Maat</h2>
          <p style={{ color: "#fff", opacity: .92, maxWidth: 620, margin: ".4rem auto 1.2rem" }}>Truth, balance, order and reciprocity — the ancient Egyptian Wu'sabat path applied to modern life.</p>
          <Link className="btn btn-gold" to="/wusabat"><Play size={16} /> Begin the path</Link>
        </div>
      </div></section>

      <Row title="Musbat-u: The 11 Laws of Maat" tagline="Ancient wisdom for modern living." color="green" items={byMood("wisdom")} to="/digital" />
      <Row title="Herbal Medicine" tagline="Plant allies for mind, body, and spirit." color="green" items={byIds(["materia_medica","adaptogens","wusabat_herbal_codex","herb_chamomile","herb_ashwagandha","herb_turmeric","herb_ginseng","herb_valerian","herb_peppermint","herb_ginkgo"])} to="/herbs" />
      <Row title="Energy & Subtle Body" tagline="Unblock. Rebalance. Restore." color="green" items={byIds(["reiki_group","sound_bath","somatic"])} to="/therapies" />
      <Row title="Mind & Meditation" tagline="Quiet the mind. Awaken clarity." color="gold" items={byIds(["nlp_coaching","meditation_pack","sitai","breathwork","nlp_practitioner"])} to="/courses" />

      {/* Trust */}
      <section className="sec pad"><div className="wrap">
        <div className="row-head gold"><span className="bar" /><h2>By Teacher: Evan Jeposa</h2><span className="tagline">NLP Trainer. Reiki Practitioner. Leadership Coach.</span></div>
        <div className="trust">
          <div className="prac-card">
            <span className="p-av">{PRACTITIONER.initials}</span>
            <div><h3 style={{ margin: 0 }}>{PRACTITIONER.name}</h3>
              <div className="eyebrow" style={{ color: "var(--muted)" }}>{PRACTITIONER.role}</div>
              <p className="muted" style={{ marginTop: ".5rem" }}>{PRACTITIONER.bio}</p>
              <div className="accred"><span>4.8★ average</span><span>NLP Trainer</span><span>Reiki Practitioner</span></div>
            </div>
          </div>
          <div className="quotes">
            {TESTIMONIALS.map((t) => (<div className="quote" key={t.name}>
              <div className="stars">{Array.from({ length: t.stars }).map((_, i) => <Star key={i} size={13} fill="currentColor" />)}</div>
              <p>“{t.quote}”</p><div className="who">{t.name}</div></div>))}
          </div>
        </div>
      </div></section>

      <Row title="Community Favourites" tagline="Loved by thousands. Rated 4.8★ and above." color="red" items={community} />

      {/* Browse by */}
      <section className="sec pad"><div className="wrap">
        <div className="row-head gold"><span className="bar" /><h2>Browse the Library</h2><span className="tagline">Explore every angle of your journey.</span></div>
        <div className="browse-groups">
          <div className="browse-col">
            <h4>By Goal</h4>
            <div className="chip-cloud">{BROWSE_GOALS.map((g) => <button key={g.id} className={`chip-lg ${g.color}`} onClick={() => navigate(`/explore?goal=${g.id}`)}>{g.label}</button>)}</div>
          </div>
          <div className="browse-col">
            <h4>By Tradition</h4>
            <div className="chip-cloud">{BROWSE_TRADITIONS.map((t) => <button key={t.id} className={`chip-lg ${t.color}`} onClick={() => navigate(`/explore?tradition=${t.id}`)}>{t.label}</button>)}</div>
          </div>
          <div className="browse-col">
            <h4>By Mood</h4>
            <div className="chip-cloud">{BROWSE_MOODS.map((m) => <button key={m.id} className={`chip-lg ${m.color}`} onClick={() => navigate(`/explore?mood=${m.id}`)}>{m.label}</button>)}</div>
          </div>
        </div>
      </div></section>

      {/* Membership */}
      <section className="sec pad"><div className="wrap">
        <div className="band gold">
          <span className="eyebrow" style={{ color: "#241c05" }}>⭐ ELY PLUS MEMBERSHIP</span>
          <h2 style={{ color: "#241c05", marginTop: ".4rem" }}>Your journey continues here</h2>
          <p style={{ color: "#3a2f10", maxWidth: 560, margin: ".4rem auto 1.2rem", fontWeight: 700 }}>Full library access, live monthly sessions, member prices and saved progress — £19/month.</p>
          <button className="btn btn-primary" onClick={() => buy({ id: "ely_plus", category: "membership", title: "ELY Plus" })}>Join ELY Plus</button>
          <form onSubmit={submitLead} style={{ display: "flex", gap: ".5rem", maxWidth: 420, margin: "1.4rem auto 0", flexWrap: "wrap" }}>
            <input value={email} onChange={(e) => setEmail(e.target.value)} required type="email" placeholder="Free wellbeing tips by email" style={{ flex: 1, minWidth: 200, border: 0, borderRadius: 6, padding: ".8rem 1.1rem" }} />
            <button className="btn btn-green" type="submit">Subscribe</button>
          </form>
        </div>
      </div></section>

      <Footer />
    </>
  );
}
